// src/SelectContactsModal.jsx
// FINAL - FITS contacts_routes.py + branch_tables.py + editable selected contacts
// ✅ GET /contacts?search=...&search_in=...
// ✅ Matches backend Lebanese/international normalization logic
// ✅ Accepts backend shape { items } / { contacts } / []
// ✅ Saves exact payload shape expected by /save-table
// ✅ Sends x-branch header
// ✅ Keeps birthday as --MM-DD
// ✅ Safe modal-friendly DatePicker
// ✅ Add / edit / remove selected contacts before saving
// ✅ Frontend dedupe consistent with backend alias matching logic
// ✅ More user-friendly advanced birthday picker
// ✅ Month chooser text black
// ✅ Quick Today / Clear buttons
// ✅ Cleaner weekday labels + better calendar header

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./ModalStyles.css";
import { useRefresh } from "./context/RefreshContext";
import countryCodes from "./utils/countryCodes";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  API as DEFAULT_API,
  SERVICE_API,
  apiJSON,
  getActiveBranch,
} from "./apiBase";

const VALID_BRANCHES = ["jounieh", "tripoli", "batroun", "zgharta", "ehden"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* ============================================================================
   Helpers
============================================================================ */
function digits(v) {
  return String(v || "").replace(/\D/g, "");
}

function phoneTokens(v) {
  return String(v || "")
    .split(/\D+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizePhoneCanonical(value) {
  let d = digits(value);
  if (!d) return "";

  if (d.startsWith("00")) {
    d = d.slice(2);
  }

  if (d.startsWith("961")) {
    let rest = d.slice(3);

    while (rest.startsWith("0")) {
      rest = rest.slice(1);
    }

    if (!rest) return "961";

    if (rest.length > 8) {
      rest = rest.slice(-8).replace(/^0+/, "");
      if (!rest) return "";
    }

    return `961${rest}`;
  }

  if (d.startsWith("0") && (d.length === 7 || d.length === 8)) {
    const rest = d.slice(1);
    return rest ? `961${rest}` : "";
  }

  if (d.length === 7 || d.length === 8) {
    return `961${d}`;
  }

  return d;
}

function buildPhoneAliases(value) {
  const out = new Set();
  const canonical = normalizePhoneCanonical(value);
  if (!canonical) return out;

  const d = digits(canonical);
  out.add(canonical);
  out.add(d);

  if (canonical.length >= 8) {
    out.add(canonical.slice(-8));
  }

  if (canonical.startsWith("961")) {
    const local = canonical.slice(3);
    if (local) {
      out.add(local);
      out.add(`0${local}`);
      out.add(`961${local}`);
      out.add(`+961${local}`);
    }
  } else {
    out.add(`+${d}`);
  }

  return new Set([...out].filter(Boolean));
}

function extractRowPhoneCandidates(rawPhones) {
  const tokens = phoneTokens(rawPhones);
  const seen = new Set();
  const out = [];

  for (const tok of tokens) {
    const n = normalizePhoneCanonical(tok);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }

  if (!out.length && rawPhones) {
    const n = normalizePhoneCanonical(rawPhones);
    if (n) out.push(n);
  }

  return out;
}

function phoneAliasesIntersect(a, b) {
  const aa = buildPhoneAliases(a);
  const bb = buildPhoneAliases(b);

  for (const x of aa) {
    if (bb.has(x)) return true;
  }
  return false;
}

function normalizeBirthdayToMMDD(v) {
  const s = String(v || "").trim();
  if (!s) return "";

  let m = s.match(/^--(\d{2})-(\d{2})$/);
  if (m) return `--${m[1]}-${m[2]}`;

  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `--${m[2]}-${m[3]}`;

  m = s.match(/^(\d{2})-(\d{2})$/);
  if (m) return `--${m[1]}-${m[2]}`;

  m = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?$/);
  if (m) {
    const dd = String(Number(m[1])).padStart(2, "0");
    const mm = String(Number(m[2])).padStart(2, "0");
    return `--${mm}-${dd}`;
  }

  return s;
}

function birthdayStringToDate(value) {
  const s = String(value || "").trim();
  if (!s) return null;

  if (/^--\d{2}-\d{2}$/.test(s)) return new Date(`2000-${s.slice(2)}`);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s);
  if (/^\d{2}-\d{2}$/.test(s)) return new Date(`2000-${s}`);
  return null;
}

function birthdayDateToMMDD(date) {
  if (!date) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `--${mm}-${dd}`;
}

function birthdayDisplayText(value) {
  const d = birthdayStringToDate(value);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  });
}

function normalizeContactForTable(c) {
  const phones = normalizePhoneCanonical(c?.phones || c?.phone || "");

  return {
    id: c?.id ?? null,
    name: String(c?.name || "").trim(),
    phones,
    email: String(c?.email || "").trim(),
    company: String(c?.company || "").trim(),
    title: String(c?.title || "").trim(),
    birthday: normalizeBirthdayToMMDD(c?.birthday || ""),
    address: String(c?.address || "").trim(),
    added_at:
      String(c?.added_at || "").trim() ||
      new Date().toISOString().slice(0, 19).replace("T", " "),
    status: String(c?.status || "").trim(),
    pdf_file: String(c?.pdf_file || "").trim(),
    normalized_phones: Array.isArray(c?.normalized_phones)
      ? c.normalized_phones
      : extractRowPhoneCandidates(c?.phones || c?.phone || ""),
  };
}

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : "";
  } catch {
    return "";
  }
}

function pickBranch(...vals) {
  for (const v of vals) {
    const b = String(v || "").trim().toLowerCase();
    if (VALID_BRANCHES.includes(b)) return b;
  }
  return "";
}

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, {
    mode: "cors",
    credentials: "include",
    ...opts,
  });

  const ct = res.headers.get("content-type") || "";
  let payload = null;

  if (ct.includes("application/json")) {
    payload = await res.json().catch(() => null);
  } else {
    payload = await res.text().catch(() => "");
  }

  if (!res.ok) {
    const msg =
      (payload && payload.error) ||
      (payload && payload.detail) ||
      (payload && payload.message) ||
      (typeof payload === "string" ? payload : "") ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

function renderAdvancedMonthHeader({
  date,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) {
  return (
    <div
      style={{
        margin: "8px 10px 4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        style={{
          border: "1px solid #d1d5db",
          background: "#ffffff",
          color: "#000000",
          borderRadius: 10,
          width: 34,
          height: 34,
          cursor: prevMonthButtonDisabled ? "not-allowed" : "pointer",
          fontWeight: 700,
        }}
        title="Previous month"
      >
        ‹
      </button>

      <select
        value={date.getMonth()}
        onChange={({ target: { value } }) => changeMonth(Number(value))}
        style={{
          color: "#000000",
          background: "#ffffff",
          border: "1px solid #d1d5db",
          borderRadius: 10,
          padding: "7px 12px",
          fontSize: 14,
          fontWeight: 700,
          outline: "none",
          minWidth: 160,
        }}
        title="Choose month"
      >
        {MONTHS.map((month, index) => (
          <option key={month} value={index} style={{ color: "#000000" }}>
            {month}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        style={{
          border: "1px solid #d1d5db",
          background: "#ffffff",
          color: "#000000",
          borderRadius: 10,
          width: 34,
          height: 34,
          cursor: nextMonthButtonDisabled ? "not-allowed" : "pointer",
          fontWeight: 700,
        }}
        title="Next month"
      >
        ›
      </button>
    </div>
  );
}

function BirthdayPicker({
  value,
  onChange,
  placeholder = "Select Month and Day",
  autoOpen = false,
  requiredWarning = false,
}) {
  const selectedDate = birthdayStringToDate(value);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!autoOpen) return;

    const timer = window.setTimeout(() => {
      pickerRef.current?.setOpen?.(true);
    }, 80);

    return () => window.clearTimeout(timer);
  }, [autoOpen]);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {requiredWarning && !value ? (
        <div
          role="alert"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #ef4444",
            background: "#fee2e2",
            color: "#991b1b",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          ⚠️ This guest has no birthday in the system. Birthday is mandatory.
        </div>
      ) : null}

      <DatePicker
        ref={pickerRef}
        selected={selectedDate}
        onChange={(date) => onChange(date ? birthdayDateToMMDD(date) : "")}
        dateFormat="MMMM d"
        showPopperArrow={false}
        placeholderText={placeholder}
        popperPlacement="bottom-start"
        withPortal={window.innerWidth < 768}
        portalId="root"
        autoComplete="off"
        shouldCloseOnSelect
        fixedHeight
        peekNextMonth
        calendarStartDay={1}
        disabledKeyboardNavigation={false}
        onKeyDown={(e) => e.preventDefault()}
        renderCustomHeader={renderAdvancedMonthHeader}
        formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
        dayClassName={(date) => {
          const today = new Date();
          const isToday =
            date.getMonth() === today.getMonth() && date.getDate() === today.getDate();

          return isToday ? "birthday-day-today" : "birthday-day-normal";
        }}
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn mini"
          onClick={() => onChange(birthdayDateToMMDD(new Date()))}
        >
          Today
        </button>

        <button
          type="button"
          className="btn ghost mini"
          onClick={() => onChange("")}
        >
          Clear
        </button>

        {value ? (
          <small style={{ opacity: 0.85, alignSelf: "center" }}>
            Selected: {birthdayDisplayText(value)} ({value})
          </small>
        ) : (
          <small style={{ opacity: 0.65, alignSelf: "center" }}>
            Stores as --MM-DD
          </small>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
   Component
============================================================================ */
export default function SelectContactsModal({ tableMeta, onClose }) {
  const { triggerRefresh } =
    typeof useRefresh === "function"
      ? useRefresh()
      : { triggerRefresh: () => {} };

  const tableApi = DEFAULT_API;
  const LOYALTY_API =
    SERVICE_API && SERVICE_API.loyalty ? SERVICE_API.loyalty : DEFAULT_API;

  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [editingPhone, setEditingPhone] = useState("");
  const [editDraft, setEditDraft] = useState(null);
  const [missingBirthdayPhone, setMissingBirthdayPhone] = useState("");
  const [birthdayPopupContact, setBirthdayPopupContact] = useState(null);
  const [birthdayPopupValue, setBirthdayPopupValue] = useState("");
  const [showNewContactForm, setShowNewContactForm] = useState(false);

  const [newContact, setNewContact] = useState({
    name: "",
    code: "961",
    phones: "",
    email: "",
    company: "",
    title: "",
    birthday: "",
    address: "",
  });

  const branchHeader = useMemo(() => {
    return pickBranch(
      tableMeta?.branch,
      tableMeta?.city,
      tableMeta?.x_branch,
      getActiveBranch?.()
    );
  }, [tableMeta]);

  const csrfCookie =
    getCookie("csrf") || getCookie("csrf_token") || getCookie("XSRF-TOKEN") || "";

  const authHeaders = useCallback(
    (extra = {}) => ({
      ...(branchHeader ? { "x-branch": branchHeader, "X-Branch": branchHeader } : {}),
      ...(csrfCookie ? { "x-csrf-token": csrfCookie, "X-CSRF-Token": csrfCookie } : {}),
      ...extra,
    }),
    [branchHeader, csrfCookie]
  );

  const dedupeContacts = useCallback((list) => {
    const out = [];

    for (const raw of list) {
      const c = normalizeContactForTable(raw);

      if (!c.phones) {
        const emptyExists = out.some(
          (x) =>
            !x.phones &&
            String(x.name || "").trim().toLowerCase() ===
              String(c.name || "").trim().toLowerCase()
        );
        if (!emptyExists) out.push(c);
        continue;
      }

      const exists = out.some((x) => x.phones && phoneAliasesIntersect(x.phones, c.phones));
      if (!exists) out.push(c);
    }

    return out;
  }, []);

  const isDuplicatePhone = useCallback(
    (phone, exceptPhone = "") => {
      const normalized = normalizePhoneCanonical(phone);
      if (!normalized) return false;

      return selected.some((c) => {
        if (exceptPhone && phoneAliasesIntersect(c.phones, exceptPhone)) return false;
        return phoneAliasesIntersect(c.phones, normalized);
      });
    },
    [selected]
  );

  const today = new Date();

  const noon = (d) => {
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x;
  };

  const parseBirthday = (bday) => {
    if (!bday) return null;
    const s = String(bday).trim();

    let m = s.match(/^--(\d{2})-(\d{2})$/);
    if (m) return { month: +m[1], day: +m[2] };

    m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return { month: +m[2], day: +m[3] };

    m = s.match(/^(\d{2})-(\d{2})$/);
    if (m) return { month: +m[1], day: +m[2] };

    m = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?$/);
    if (m) return { month: +m[2], day: +m[1] };

    return null;
  };

  const daysUntilNext = (mm, dd) => {
    const base = noon(today);
    const y = base.getFullYear();
    let next = noon(new Date(y, mm - 1, dd));
    if (next < base) next = noon(new Date(y + 1, mm - 1, dd));
    return Math.round((next - base) / 86400000);
  };

  const daysSinceLast = (mm, dd) => {
    const base = noon(today);
    const y = base.getFullYear();
    let prev = noon(new Date(y, mm - 1, dd));
    if (prev > base) prev = noon(new Date(y - 1, mm - 1, dd));
    return Math.round((base - prev) / 86400000);
  };

  const decorateContact = useCallback((c) => {
    const normalized = normalizeContactForTable(c);
    const b = parseBirthday(normalized.birthday);

    if (!b) return { ...normalized, _bdayInfo: null };

    const until = daysUntilNext(b.month, b.day);
    const since = daysSinceLast(b.month, b.day);
    const mm = String(b.month).padStart(2, "0");
    const dd = String(b.day).padStart(2, "0");

    return {
      ...normalized,
      _bdayInfo: {
        month: b.month,
        day: b.day,
        until,
        since,
        inNextWeek: until >= 0 && until <= 7,
        inLastMonth: since > 0 && since <= 30,
        pretty: `--${mm}-${dd}`,
      },
    };
  }, []);

  useEffect(() => {
    if (search.trim().length < 1) {
      setContacts([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        setNotice(null);

        const trimmed = search.trim();
        const d = digits(trimmed);
        const searchIn = d ? "phone" : "all";

        const url =
          `${tableApi}/contacts` +
          `?search=${encodeURIComponent(trimmed)}` +
          `&search_in=${encodeURIComponent(searchIn)}` +
          `&limit=50&offset=0`;

        const data = await fetchJSON(url, {
          method: "GET",
          headers: authHeaders(),
          signal: controller.signal,
        });

        if (cancelled) return;

        const rows = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.contacts)
          ? data.contacts
          : Array.isArray(data)
          ? data
          : [];

        const decorated = rows
          .map((r) => decorateContact(r))
          .filter((c) => !isDuplicatePhone(c.phones));

        setContacts(dedupeContacts(decorated));
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error("Fetch /contacts failed:", e);
        if (cancelled) return;

        if (e?.status === 401) {
          setNotice("Not authenticated. Please login again.");
        } else if (e?.status === 403) {
          setNotice(`Forbidden for branch '${String(branchHeader || "")}'.`);
        } else {
          setNotice(e?.message || "Failed to fetch contacts.");
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [search, branchHeader, tableApi, authHeaders, decorateContact, isDuplicatePhone, dedupeContacts]);

  const addToTable = (contact) => {
    const normalized = normalizeContactForTable(contact);
    if (!normalized.phones) return;

    if (!isDuplicatePhone(normalized.phones)) {
      setSelected((prev) => dedupeContacts([...prev, normalized]));

      if (!normalized.birthday) {
        setMissingBirthdayPhone(normalized.phones);
        setBirthdayPopupContact(normalized);
        setBirthdayPopupValue("");
        setNotice(null);
      }
    }
  };

  const saveMissingBirthday = () => {
    if (!birthdayPopupContact) return;

    const birthday = normalizeBirthdayToMMDD(birthdayPopupValue);
    if (!birthday) {
      window.alert("Please select the guest birthday.");
      return;
    }

    setSelected((prev) =>
      prev.map((contact) =>
        phoneAliasesIntersect(contact.phones, birthdayPopupContact.phones)
          ? { ...contact, birthday }
          : contact
      )
    );

    setContacts((prev) =>
      prev.map((contact) =>
        phoneAliasesIntersect(contact.phones, birthdayPopupContact.phones)
          ? decorateContact({ ...contact, birthday })
          : contact
      )
    );

    setBirthdayPopupContact(null);
    setBirthdayPopupValue("");
    setMissingBirthdayPhone("");
    setNotice(`Birthday added for ${birthdayPopupContact.name || "the guest"}.`);
  };

  const closeMissingBirthdayPopup = () => {
    if (birthdayPopupContact) {
      setSelected((prev) =>
        prev.filter(
          (contact) =>
            !phoneAliasesIntersect(contact.phones, birthdayPopupContact.phones)
        )
      );
    }

    setBirthdayPopupContact(null);
    setBirthdayPopupValue("");
    setMissingBirthdayPhone("");
  };

  const removeFromTable = (phones) => {
    setSelected((prev) => prev.filter((c) => !phoneAliasesIntersect(c.phones, phones)));
    if (editingPhone && phoneAliasesIntersect(editingPhone, phones)) {
      setEditingPhone("");
      setEditDraft(null);
    }
  };

  const startEditContact = (contact) => {
    setEditingPhone(contact.phones || `tmp-${Date.now()}`);
    setEditDraft({
      id: contact.id ?? null,
      name: contact.name || "",
      code: contact.phones?.startsWith("961") ? "961" : "",
      phones: contact.phones?.startsWith("961") ? contact.phones.slice(3) : contact.phones || "",
      email: contact.email || "",
      company: contact.company || "",
      title: contact.title || "",
      birthday: contact.birthday || "",
      address: contact.address || "",
      originalPhones: contact.phones || "",
    });
  };

  const cancelEditContact = () => {
    if (editDraft && !normalizeBirthdayToMMDD(editDraft.birthday)) {
      window.alert("Birthday is mandatory. Please select the guest birthday first.");
      return;
    }

    setEditingPhone("");
    setEditDraft(null);
    setMissingBirthdayPhone("");
  };

  const saveEditedContact = () => {
    if (!editDraft) return;

    if (!String(editDraft.name || "").trim()) {
      window.alert("Name is required.");
      return;
    }

    if (!normalizeBirthdayToMMDD(editDraft.birthday)) {
      setMissingBirthdayPhone(editDraft.originalPhones || editingPhone);
      setNotice("Birthday is mandatory. Please select a birthday from the calendar.");
      window.alert("Birthday is mandatory. Please select a birthday from the calendar.");
      return;
    }

    const rawPhone =
      String(editDraft.code || "").trim() === "961"
        ? editDraft.phones
        : `${editDraft.code || ""}${editDraft.phones || ""}`;

    const updated = normalizeContactForTable({
      id: editDraft.id,
      name: editDraft.name,
      phones: rawPhone,
      email: editDraft.email,
      company: editDraft.company,
      title: editDraft.title,
      birthday: editDraft.birthday,
      address: editDraft.address,
    });

    if (!updated.phones) {
      window.alert("Invalid phone number.");
      return;
    }

    if (isDuplicatePhone(updated.phones, editDraft.originalPhones)) {
      window.alert("Another selected contact already uses this phone.");
      return;
    }

    setSelected((prev) =>
      dedupeContacts(
        prev.map((c) =>
          phoneAliasesIntersect(c.phones, editDraft.originalPhones) ? updated : c
        )
      )
    );

    setEditingPhone("");
    setEditDraft(null);
    setMissingBirthdayPhone("");
    setNotice(null);
  };

  const handleNewContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };

  const addNewContact = () => {
    if (!newContact.name.trim() || !newContact.phones.trim()) {
      window.alert("Name and Phone are required.");
      return;
    }

    if (!normalizeBirthdayToMMDD(newContact.birthday)) {
      window.alert("Birthday is mandatory.");
      return;
    }

    const raw = normalizeContactForTable({
      name: newContact.name,
      phones:
        String(newContact.code || "961").trim() === "961"
          ? newContact.phones
          : `${newContact.code}${newContact.phones}`,
      email: newContact.email,
      company: newContact.company,
      title: newContact.title,
      birthday: newContact.birthday,
      address: newContact.address,
    });

    if (!raw.phones) {
      window.alert("Invalid phone number.");
      return;
    }

    if (isDuplicatePhone(raw.phones)) {
      window.alert("This contact (same phone) is already added.");
      return;
    }

    setSelected((prev) => dedupeContacts([...prev, raw]));
    setNewContact({
      name: "",
      code: "961",
      phones: "",
      email: "",
      company: "",
      title: "",
      birthday: "",
      address: "",
    });
    setShowNewContactForm(false);
    setNotice(`${raw.name || "Contact"} was added successfully.`);
  };

  async function loyaltySearchByPhone(phone) {
    const u = `${LOYALTY_API}/loyalty/customers/search?q=${encodeURIComponent(phone)}`;
    const j = await fetchJSON(u, { method: "GET", headers: authHeaders() });

    const targetAliases = buildPhoneAliases(phone);
    const exact = (j?.results || []).find((c) => {
      const aliases = buildPhoneAliases(c?.phone || "");
      for (const a of aliases) {
        if (targetAliases.has(a)) return true;
      }
      return false;
    });

    return exact || null;
  }

  async function loyaltyEnsureCustomer({ name, phone }) {
    const found = await loyaltySearchByPhone(phone).catch(() => null);
    if (found) return found;

    const j = await fetchJSON(`${LOYALTY_API}/loyalty/customers`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ name: name || null, phone }),
    });

    return j?.customer || j;
  }

  async function loyaltyRecordVisit({ customer_id, table_number, points = 5 }) {
    return fetchJSON(`${LOYALTY_API}/loyalty/visit`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ customer_id, table_number, points }),
    });
  }

  async function awardLoyaltyForContacts(uniqueContacts) {
    const pool = 4;
    let idx = 0;
    let ok = 0;
    let fail = 0;

    async function next() {
      if (idx >= uniqueContacts.length) return;

      const i = idx++;
      const c = uniqueContacts[i];
      const name = c.name || "";
      const phone = normalizePhoneCanonical(c.phones);

      if (!phone) {
        fail++;
        return next();
      }

      try {
        const cust = await loyaltyEnsureCustomer({ name, phone });
        await loyaltyRecordVisit({
          customer_id: cust.id,
          table_number: tableMeta?.table_number || undefined,
          points: 5,
        });
        ok++;
      } catch (e) {
        console.warn("Loyalty award failed for", phone, e);
        fail++;
      }

      return next();
    }

    const workers = Array.from(
      { length: Math.min(pool, uniqueContacts.length) },
      () => next()
    );

    await Promise.all(workers);
    return { ok, fail };
  }

  const saveTable = async () => {
    if (!selected.length) {
      window.alert("Add at least one contact.");
      return;
    }

    if (!tableApi) {
      window.alert("Missing API base. Please check API configuration.");
      return;
    }

    if (!branchHeader) {
      window.alert("Missing branch. Please select branch.");
      return;
    }

    const tableName = String(tableMeta?.table_number || "").trim();
    if (!tableName) {
      window.alert("Missing table number.");
      return;
    }

    const missingBirthdayContact = selected.find(
      (c) => !normalizeBirthdayToMMDD(c?.birthday)
    );

    if (missingBirthdayContact) {
      setMissingBirthdayPhone(missingBirthdayContact.phones || "");
      setBirthdayPopupContact(missingBirthdayContact);
      setBirthdayPopupValue("");
      setNotice(null);
      return;
    }

    setSaving(true);
    setNotice(null);

    try {
      const uniqueContacts = dedupeContacts(
        selected.map((c) => normalizeContactForTable(c))
      );

      const payload = {
        metadata: {
          hostess: String(tableMeta?.hostess || "").trim(),
          waiter: String(tableMeta?.waiter || "").trim(),
          table_number: tableName,
        },
        contacts: uniqueContacts.map((c) => ({
          name: c.name || "",
          phones: c.phones || "",
          email: c.email || "",
          company: c.company || "",
          title: c.title || "",
          birthday: c.birthday || "",
          address: c.address || "",
          added_at: c.added_at || "",
        })),
      };

      const out = await apiJSON("/save-table", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      let loyaltySummary = null;
      try {
        const { ok, fail } = await awardLoyaltyForContacts(uniqueContacts);
        loyaltySummary =
          `Loyalty: +5 awarded to ${ok} contact(s)` + (fail ? `, ${fail} failed.` : ".");
      } catch {
        loyaltySummary = "Loyalty awarding encountered an error.";
      }

      setNotice(loyaltySummary || out?.message || "Saved.");
      setSaving(false);

      if (triggerRefresh) triggerRefresh();
      onClose();
    } catch (err) {
      console.error("Save table failed:", err);

      let msg = "Failed to save table.";
      if (err?.status === 401) msg = "Not authenticated. Please login again.";
      else if (err?.status === 403) msg = `Forbidden for branch '${String(branchHeader)}'.`;
      else if (err?.message) msg = err.message;

      setNotice(msg);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <h2 style={{ margin: 0 }}>Select Contacts</h2>
            <button className="btn ghost small" onClick={onClose} type="button">
              ✕
            </button>
          </div>

          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 8 }}>
            Saving to: <code>{tableApi}</code>
            <br />
            Loyalty API: <code>{LOYALTY_API}</code>
            {branchHeader ? (
              <>
                <br />
                Branch: <code>{branchHeader}</code>
              </>
            ) : null}
          </div>

          {notice && (
            <div className="info-box" style={{ marginTop: 10 }}>
              {notice}
            </div>
          )}
        </div>

        <div className="modal-body">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone"
            autoFocus
          />

          <h4>🔎 Search Results</h4>
          <div className="contact-list">
            {contacts.map((c, i) => (
              <div key={`${c.id || "x"}-${c.phones || i}`} className="contact-row">
                <div>
                  <div>
                    <strong>{c.name || "(No Name)"}</strong>
                  </div>

                  <div>
                    <small>{c.phones || "—"}</small>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {c.birthday ? (
                      <small>🎂 {c._bdayInfo?.pretty || c.birthday}</small>
                    ) : (
                      <small>🎂 —</small>
                    )}

                    {c._bdayInfo?.inNextWeek && <span style={badgeStyle}>🎉 Next 7 days</span>}
                    {c._bdayInfo?.inLastMonth && <span style={badgeStyleAlt}>🎂 Last month</span>}
                  </div>
                </div>

                <button
                  className="btn mini"
                  onClick={() => addToTable(c)}
                  disabled={isDuplicatePhone(c.phones)}
                  type="button"
                >
                  {isDuplicatePhone(c.phones) ? "✅ Added" : "➕ Add"}
                </button>
              </div>
            ))}

            {contacts.length === 0 && search.trim().length >= 1 && (
              <div className="empty-hint">No results.</div>
            )}
          </div>

          <h4>✅ Added to Table</h4>
          <div className="contact-list">
            {selected.map((c, i) => {
              const isEditing = editingPhone && phoneAliasesIntersect(editingPhone, c.phones);

              if (isEditing && editDraft) {
                return (
                  <div key={`${c.id || "s"}-${c.phones || i}`} className="contact-row selected" style={{ alignItems: "stretch" }}>
                    <div style={{ width: "100%" }}>
                      <input
                        value={editDraft.name}
                        onChange={(e) => setEditDraft((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Name"
                      />

                      <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center" }}>
                        <div style={{ minWidth: 170 }}>
                          <Select
                            options={countryCodes.map((cc) => ({
                              value: cc.code,
                              label: cc.label,
                            }))}
                            value={{
                              value: editDraft.code || "961",
                              label:
                                countryCodes.find((cc) => cc.code === (editDraft.code || "961"))?.label ||
                                "Lebanon (+961)",
                            }}
                            onChange={(opt) =>
                              setEditDraft((p) => ({
                                ...p,
                                code: opt?.value || "961",
                              }))
                            }
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 10060 }),
                            }}
                          />
                        </div>

                        <input
                          value={editDraft.phones}
                          onChange={(e) => setEditDraft((p) => ({ ...p, phones: e.target.value }))}
                          placeholder="Phone"
                          style={{ flex: 1 }}
                        />
                      </div>

                      <input
                        value={editDraft.email}
                        onChange={(e) => setEditDraft((p) => ({ ...p, email: e.target.value }))}
                        placeholder="Email"
                        style={{ marginTop: 8 }}
                      />

                      <input
                        value={editDraft.company}
                        onChange={(e) => setEditDraft((p) => ({ ...p, company: e.target.value }))}
                        placeholder="Company"
                        style={{ marginTop: 8 }}
                      />

                      <input
                        value={editDraft.title}
                        onChange={(e) => setEditDraft((p) => ({ ...p, title: e.target.value }))}
                        placeholder="Title"
                        style={{ marginTop: 8 }}
                      />

                      <div style={{ marginTop: 8 }}>
                        <label style={{ display: "block", marginBottom: 6 }}>Birthday</label>
                        <BirthdayPicker
                          value={editDraft.birthday}
                          autoOpen={
                            !!missingBirthdayPhone &&
                            phoneAliasesIntersect(
                              missingBirthdayPhone,
                              editDraft.originalPhones || editingPhone
                            )
                          }
                          requiredWarning={
                            !!missingBirthdayPhone &&
                            phoneAliasesIntersect(
                              missingBirthdayPhone,
                              editDraft.originalPhones || editingPhone
                            )
                          }
                          onChange={(nextBirthday) => {
                            setEditDraft((p) => ({
                              ...p,
                              birthday: nextBirthday,
                            }));

                            if (nextBirthday) {
                              setMissingBirthdayPhone("");
                              setNotice(null);
                            }
                          }}
                        />
                      </div>

                      <input
                        value={editDraft.address}
                        onChange={(e) => setEditDraft((p) => ({ ...p, address: e.target.value }))}
                        placeholder="Address"
                        style={{ marginTop: 8 }}
                      />

                      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                        <button className="btn primary mini" onClick={saveEditedContact} type="button">
                          💾 Save Edit
                        </button>
                        <button className="btn ghost mini" onClick={cancelEditContact} type="button">
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={`${c.id || "s"}-${c.phones || i}`} className="contact-row selected">
                  <div>
                    <strong>{c.name || "(No Name)"}</strong>
                    <br />
                    <small>{c.phones || "—"}</small>
                    {c.company ? (
                      <>
                        <br />
                        <small>🏢 {c.company}</small>
                      </>
                    ) : null}
                    {c.birthday ? (
                      <>
                        <br />
                        <small>🎂 {c.birthday}</small>
                      </>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      className="btn mini"
                      onClick={() => startEditContact(c)}
                      type="button"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn danger mini"
                      onClick={() => removeFromTable(c.phones)}
                      type="button"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}

            {selected.length === 0 && <div className="empty-hint">Nothing added yet.</div>}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              marginTop: 18,
              marginBottom: 10,
            }}
          >
            <h4 style={{ margin: 0 }}>➕ New Contact</h4>
            <button
              className="btn primary"
              type="button"
              onClick={() => {
                setShowNewContactForm(true);
                setNotice(null);
              }}
            >
              ➕ Add New
            </button>
          </div>

          <div style={{ height: 12 }} />
        </div>

        <div className="modal-footer">
          <div className="footer-actions">
            <button onClick={onClose} className="btn ghost" type="button" disabled={saving}>
              ❌ Cancel
            </button>

            <button onClick={saveTable} className="btn primary" disabled={saving} type="button">
              {saving ? "💾 Saving…" : `💾 Save Table (${selected.length})`}
            </button>
          </div>
        </div>
      </div>

      {birthdayPopupContact ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="missing-birthday-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 12000,
            background: "rgba(15, 23, 42, 0.72)",
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(440px, 100%)",
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              background: "#ffffff",
              color: "#111827",
              borderRadius: 18,
              boxShadow: "0 24px 70px rgba(0,0,0,.35)",
              padding: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div>
                <h3 id="missing-birthday-title" style={{ margin: 0 }}>
                  Birthday Missing
                </h3>
                <p style={{ margin: "7px 0 0", color: "#6b7280" }}>
                  {birthdayPopupContact.name || "This guest"} has no birthday in
                  the system. Please select the birthday to continue.
                </p>
              </div>
              <button
                className="btn ghost small"
                type="button"
                onClick={closeMissingBirthdayPopup}
                aria-label="Close birthday popup"
              >
                ✕
              </button>
            </div>

            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: "11px 13px",
                borderRadius: 12,
                border: "1px solid #f59e0b",
                background: "#fffbeb",
                color: "#92400e",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              ⚠️ Birthday is missing.
            </div>

            <div style={{ marginTop: 16 }}>
              <BirthdayPicker
                value={birthdayPopupValue}
                autoOpen
                requiredWarning={false}
                onChange={setBirthdayPopupValue}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 18,
                flexWrap: "wrap",
              }}
            >
              <button
                className="btn ghost"
                type="button"
                onClick={closeMissingBirthdayPopup}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                type="button"
                onClick={saveMissingBirthday}
                disabled={!birthdayPopupValue}
              >
                Save Birthday
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showNewContactForm ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-contact-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 11900,
            background: "rgba(15, 23, 42, 0.72)",
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(620px, 100%)",
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              background: "#ffffff",
              color: "#111827",
              borderRadius: 18,
              boxShadow: "0 24px 70px rgba(0,0,0,.35)",
              padding: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h3 id="new-contact-title" style={{ margin: 0 }}>
                  Add New Contact
                </h3>
                <p style={{ margin: "7px 0 0", color: "#6b7280" }}>
                  Enter the contact information below.
                </p>
              </div>
              <button
                className="btn ghost small"
                type="button"
                onClick={() => setShowNewContactForm(false)}
                aria-label="Close new contact popup"
              >
                ✕
              </button>
            </div>

            <div className="contact-form">
              <input
                name="name"
                value={newContact.name}
                onChange={handleNewContactChange}
                placeholder="Name *"
                autoFocus
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(170px, 220px) minmax(0, 1fr)",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <Select
                  options={countryCodes.map((c) => ({
                    value: c.code,
                    label: c.label,
                  }))}
                  value={{
                    value: newContact.code,
                    label:
                      countryCodes.find((c) => c.code === newContact.code)?.label ||
                      "Lebanon (+961)",
                  }}
                  onChange={(opt) =>
                    setNewContact((prev) => ({
                      ...prev,
                      code: opt?.value || "961",
                    }))
                  }
                  placeholder="Search Country"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 13000 }),
                  }}
                />

                <input
                  name="phones"
                  value={newContact.phones}
                  onChange={handleNewContactChange}
                  placeholder="Phone *"
                  inputMode="tel"
                />
              </div>

              <input
                name="email"
                value={newContact.email}
                onChange={handleNewContactChange}
                placeholder="Email"
                type="email"
              />
              <input
                name="company"
                value={newContact.company}
                onChange={handleNewContactChange}
                placeholder="Company"
              />
              <input
                name="title"
                value={newContact.title}
                onChange={handleNewContactChange}
                placeholder="Title"
              />

              <div>
                <label style={{ display: "block", marginBottom: 7 }}>
                  Birthday *
                </label>
                <BirthdayPicker
                  value={newContact.birthday}
                  requiredWarning={!newContact.birthday}
                  onChange={(nextBirthday) =>
                    setNewContact((prev) => ({
                      ...prev,
                      birthday: nextBirthday,
                    }))
                  }
                />
              </div>

              <input
                name="address"
                value={newContact.address}
                onChange={handleNewContactChange}
                placeholder="Address"
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 6,
                }}
              >
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => {
                    setShowNewContactForm(false);
                    setNewContact({
                      name: "",
                      code: "961",
                      phones: "",
                      email: "",
                      company: "",
                      title: "",
                      birthday: "",
                      address: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={addNewContact}
                  type="button"
                >
                  💾 Save Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const badgeStyle = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "#1f3147",
  border: "1px solid #335c81",
  display: "inline-block",
};

const badgeStyleAlt = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "#2a3b22",
  border: "1px solid #3c5a2f",
  display: "inline-block",
};